import type { Features } from '@cutr/constants/cutlist-theme.ts';
import React, { PropsWithChildren } from 'react';

type FeatureContextValue = {
  features: Features;
  setFeature: <K extends keyof Features>(key: K, value: Features[K]) => void;
};

const FeatureContext = React.createContext<FeatureContextValue>({
  features: {} as Features,
  setFeature: () => undefined,
});

export const useCurrentFeaturesFromProvider = () =>
  React.useContext(FeatureContext).features;

/**
 * Sets a feature value in the provider.
 * @returns A function that can be used to set a feature value.
 * @deprecated Only used in demo environment.
 */
export const useSetFeature = () => React.useContext(FeatureContext).setFeature;

export const FeatureProvider = ({
  children,
  initialState,
}: PropsWithChildren & { initialState: Features }) => {
  const [features, setFeatures] = React.useState<Features>(initialState);

  const setFeature = React.useCallback(
    <K extends keyof Features>(key: K, value: Features[K]) => {
      setFeatures((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const value = React.useMemo(
    () => ({ features, setFeature }),
    [features, setFeature]
  );

  return (
    <FeatureContext.Provider value={value}>{children}</FeatureContext.Provider>
  );
};
